import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/therapist/Button';
import { ScrollView, View, Modal as RNModal, Dimensions } from 'react-native';
import { Divider } from 'react-native-elements';
import encryptedStorage from '@/stores/encryptedStorage';
import { ExerciseButton } from '@/views/SetCard/ExerciseButton';
import { fetchPatientsList, getPermissions } from '@/api/services/user';
import { hasInternetAsync } from '@/screens/HomeTherapist/helpers';
import { IS_WEB } from '@/utils/platform';
import { isExerciseSet } from '@/utils/isExerciseSet';
import { logError } from '@/utils/logHandling';
import { logInfo } from '@/utils/logHandling';
import { Modal } from '@/components/ui/therapist/Modal';
import { NeolexonIcon } from '@/components/NeolexonIcon';
import { Patient, SetType, WordSet } from '@/stores/store.types';
import { PressableNeolexon } from '@/components/PressableNeolexon';
import { StackNavigationProp } from '@react-navigation/stack';
import { Text } from '@/components/ui/therapist/Text';
import { TherapistStackParamList } from '@/navigations/TherapistStack';
import { useDashboardContext } from '@/contexts/DashboardContext/useDashboardContext';
import { useNavigation } from '@react-navigation/native';
import { useNotification } from '@/components/ui/therapist/Notification/NotificationProvider';
import { useStoreActions, useStoreState } from '@/stores/store';
import { useOnHover } from '@/hooks/useOnHover';
import {
  SCREEN_NAME_ACCORD_TYPE,
  API_NAME_ACCORD_TYPE,
} from '@/views/SetCard/constants';

import colors from '@/config/colors';
import theme from '@/config/theme';
import { styles } from './styles';

const ACTIVE_SETS_TYPE = {
  Wörtersets: 'activeWordsets',
  Sätzesets: 'activeSentencesets',
  Textesets: 'activeTextsets',
};

type MenuItem = {
  label: string;
  action: OnActionType;
  isDisabled?: boolean;
  isDestructive?: boolean;
};

export type OnActionType =
  | 'trash'
  | 'pencil'
  | 'copy'
  | 'results'
  | 'template'
  | 'distract'
  | 'picture';

type SetCardMenuProps = {
  appDisplayName: string;
  exerciseSetName: string;
  id: string;
  setDeleteModal: (value: { isSetActive: boolean; show: boolean }) => void;
  setInfoCopyModal: ({
    idOfDataSet,
    title,
    type,
  }: {
    idOfDataSet: string;
    title: string;
    type: string;
  }) => void;
  setShowCopyModal: (value: boolean) => void;
  type: SetType;
};

export const SetCardMenu: React.FC<SetCardMenuProps> = ({
  appDisplayName,
  exerciseSetName,
  id,
  setDeleteModal,
  setInfoCopyModal,
  setShowCopyModal,
  type,
}) => {
  const { handleHoverIn, handleHoverOut } = useOnHover();
  const { fetchPatients, patientsSyncing, selectedID, selectedPatient } =
    useDashboardContext();
  const { isTherapistDemo, permissions, toolTipNumber } = useStoreState(
    (store) => store,
  );
  const { showNotification } = useNotification();
  const { onFirstMountStoreSetup, setGlobalPopupState } = useStoreActions(
    (actions) => ({
      setGlobalPopupState: actions.setGlobalPopupState,
      onFirstMountStoreSetup: actions.onFirstMountStoreSetup,
    }),
  );
  const { navigate } =
    useNavigation<StackNavigationProp<TherapistStackParamList>>();
  const [menuPosition, setMenuPosition] = useState<{
    bottom: number;
    right: number;
  }>({ bottom: 0, right: 0 });
  const [ergebnisseTherapistDemo, setErgebnisseTherapistDemo] =
    useState<boolean>(false);
  const [showSetCardMenu, setShowCardMenu] = useState<boolean>(false);
  const ellipsisRef = useRef<View>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isSync, setIsSync] = useState(false);
  const { refresh } = useStoreState((state) => state);
  const { downloadedExercises } = useDashboardContext();

  useEffect(() => {
    let isMounted = true;
    encryptedStorage()
      .getItem(id)
      .then((localSet: any) => {
        if (!isMounted) {
          return;
        }
        setIsSync(!!localSet?.sync);
        setIsLoaded(!!localSet?.activeWordset);
      });
    return () => {
      isMounted = false;
    };
  }, [id, refresh, downloadedExercises]);

  const navigateToSet = () => {
    switch (type) {
      case 'Wörtersets':
        navigate('WordSet', { selectedID, setId: id, apiName: 'wordsets' });
        return;
      case 'Sätzesets':
        navigate('SentenceSet', {
          selectedID,
          setId: id,
          apiName: 'sentencesets',
        });
        return;
      case 'Textesets':
        navigate('TextSet', { selectedID, setId: id, apiName: 'textsets' });
        return;
      default: {
        const _exhaustive: never = type;
        return _exhaustive;
      }
    }
  };

  const navigateToTemplate = () => {
    const apiName = API_NAME_ACCORD_TYPE[type];
    const screenName = SCREEN_NAME_ACCORD_TYPE[type];

    switch (screenName) {
      case 'WordSet':
        navigate(screenName, {
          selectedID,
          setId: id,
          apiName,
          templateMode: 'create',
        });
        break;
      case 'SentenceSet':
        navigate(screenName, {
          selectedID,
          setId: id,
          apiName,
          templateMode: 'create',
        });
        break;
      case 'TextSet':
        break;
    }
  };

  const onActionPress = async (name: OnActionType) => {
    const internet = await hasInternetAsync();
    if (internet) {
      setShowCardMenu(false);
      switch (name) {
        case 'trash': {
          if (isTherapistDemo) {
            showNotification({
              type: 'error',
              title: 'Löschen in der Demo nicht möglich',
              position: 'top',
            });
          } else {
            const activeSets =
              selectedPatient?.[ACTIVE_SETS_TYPE[type] as keyof Patient] ??
              null;
            const isActiveSet =
              Array.isArray(activeSets) &&
              (activeSets as string[]).includes(id);
            setDeleteModal({
              show: true,
              isSetActive: isActiveSet,
            });
          }
          break;
        }
        case 'pencil': {
          navigateToSet();
          break;
        }
        case 'template': {
          if (isTherapistDemo) {
            showNotification({
              type: 'error',
              title: 'Vorlage speichern in der Demo nicht möglich',
              position: 'top',
            });
          } else {
            navigateToTemplate();
          }
          break;
        }
        case 'results': {
          let className = '';
          let classSet = '';
          if (type == 'Wörtersets') {
            classSet = 'Wörterset';
            className = 'wordset';
          }
          if (type == 'Sätzesets') {
            classSet = 'Sätzeset';
            className = 'sentenceset';
          }
          if (isTherapistDemo) {
            setErgebnisseTherapistDemo(true);
          } else {
            // @ts-ignore
            navigate('Ergebnisse', {
              exerciseSetName,
              exerciseSet: classSet,
              exerciseClass: className,
              setId: id,
              patientName:
                selectedPatient?.firstName + ' ' + selectedPatient?.lastName,
              patientId: selectedID,
              appDisplayName: appDisplayName,
            });
          }
          break;
        }
        case 'copy': {
          if (isTherapistDemo) {
            showNotification({
              type: 'error',
              title: 'Kopieren in der Demo nicht möglich',
              position: 'top',
            });
            break;
          } else {
            let typeTitle = '';
            const endsWithS = type.endsWith('s');
            if (endsWithS) {
              typeTitle = type.slice(0, -1);
            } else {
              typeTitle = type;
            }
            setInfoCopyModal({
              title: typeTitle,
              idOfDataSet: id,
              type: type,
            });
            setShowCopyModal(true);
          }
          break;
        }
        case 'picture': {
          let className = '';
          if (type == 'Wörtersets') {
            className = 'wordset';
          }
          if (type == 'Sätzesets') {
            className = 'sentenceset';
          }
          if (type == 'Textesets') {
            className = 'textset';
          }
          const data: any = await encryptedStorage().getItem(id);
          if (isExerciseSet(data)) {
            // @ts-ignore
            navigate('BildKarten', {
              exerciseClass: className,
              setId: id,
              cardSettings: data?.cardSettings,
            });
          } else {
            logError(
              `Cannot navigate to BildKarten, not an exercise set with id ${id}`,
            );
          }
          break;
        }
        case 'distract': {
          try {
            const hasInternet = await hasInternetAsync();
            if (hasInternet) {
              const res = await encryptedStorage().getItem(id);
              const { activeWordset, ...rest } = res as WordSet;
              await encryptedStorage().setItem(id, rest);
              const objFetchPatientList = await fetchPatientsList(true);
              await fetchPatients(
                selectedPatient,
                objFetchPatientList.patients,
              );
              await patientsSyncing(objFetchPatientList.patients, true);
              const objPermission = await getPermissions(true);
              showNotification({
                type: 'info',
                title: 'Neue Ablenker wurden generiert.',
              });
              onFirstMountStoreSetup({
                appStartResumeLock: objFetchPatientList.appStartResumeLock,
                eigentrainingLock: objFetchPatientList.eigentrainingLock,
                appLockPattern: objFetchPatientList.appLockPattern,
                patients: objFetchPatientList.patients,
                subscriptions: objPermission.subscriptions,
                permissions: objPermission.permissions,
              });
            } else {
              setGlobalPopupState({
                headerText: 'Keine Verbindung zum neolexon Server',
                bodyText:
                  'Überprüfen Sie Ihre Internetverbindung und versuchen es erneut.',
                leftButton: '',
                rightButton: '',
              });
            }
          } catch (error) {
            logError('Neue Ablenker in SetCardOptions ', error);
          }
          break;
        }
        default: {
          logInfo('ACTION BTN PRESSED');
          break;
        }
      }
    } else {
      setGlobalPopupState({
        headerText: 'Keine Verbindung zum neolexon Server',
        bodyText:
          'Überprüfen Sie Ihre Internetverbindung und versuchen es erneut.',
        leftButton: '',
        rightButton: '',
      });
    }
  };

  const canStartPracticing = isTherapistDemo
    ? toolTipNumber === 1
    : !!permissions?.exercises;

  const canPractice = !!(permissions?.exercises || isTherapistDemo);
  const canSeePictureCards = !!(permissions?.pictureCards || isTherapistDemo);
  const isTextesets = type === 'Textesets';

  let canStartTraining = true;
  if (IS_WEB) {
    const currentUrlParams = new URLSearchParams(window.location.search);
    canStartTraining =
      currentUrlParams.get('nur-eigentraining-einstellen') === null;
  }

  const isDownloaded = isLoaded;

  const universalItems: MenuItem[] = [
    {
      label: 'Set Bearbeiten',
      action: 'pencil' as OnActionType,
      isDisabled: false,
    },
    { label: 'Kopieren', action: 'copy' as OnActionType, isDisabled: false },
    {
      label: 'Löschen',
      action: 'trash' as OnActionType,
      isDestructive: true,
      isDisabled: false,
    },
  ];

  const menuItems: MenuItem[] = !canStartTraining
    ? [
        ...(!isTextesets
          ? [
              {
                label: 'Ergebnisse anzeigen',
                action: 'results' as OnActionType,
              },
              {
                label: 'Set als Vorlage speichern',
                action: 'template' as OnActionType,
              },
            ]
          : []),
        ...universalItems,
      ]
    : [
        ...(!isTextesets && canPractice
          ? [
              {
                label: 'Neue Ablenker',
                action: 'distract' as OnActionType,
                isDisabled: !isDownloaded,
              },
            ]
          : []),
        ...(!isTextesets && canStartPracticing
          ? [
              {
                label: 'Ergebnisse anzeigen',
                action: 'results' as OnActionType,
              },
            ]
          : []),
        ...(!isTextesets
          ? [
              {
                label: 'Set als Vorlage speichern',
                action: 'template' as OnActionType,
              },
            ]
          : []),
        ...(!isTextesets && canSeePictureCards
          ? [
              {
                label: 'Bildkarten',
                action: 'picture' as OnActionType,
                isDisabled: !isDownloaded,
              },
            ]
          : []),
        ...universalItems,
      ];

  return (
    <>
      <ExerciseButton
        canStartPracticing={canStartPracticing}
        id={id}
        onActionPress={(action) => {
          setShowCardMenu(false);
          onActionPress(action);
        }}
        type={type}
      />
      <PressableNeolexon
        onHoverIn={handleHoverIn}
        onHoverOut={handleHoverOut}
        ref={ellipsisRef}
        style={styles.ellipsisBtn}
        onPress={() => {
          ellipsisRef.current?.measureInWindow((x, y, width, height) => {
            setMenuPosition({
              right: Dimensions.get('window').width - (x + width),
              bottom: Dimensions.get('window').height - (y + height),
            });
            setShowCardMenu(!showSetCardMenu);
          });
        }}
      >
        <NeolexonIcon name="ellipsis" size={40} />
      </PressableNeolexon>
      <RNModal transparent visible={showSetCardMenu}>
        <PressableNeolexon
          style={{ flex: 1 }}
          onPress={() => setShowCardMenu(!showSetCardMenu)}
        />
        <View
          style={[
            styles.menuWrapper,
            {
              right: menuPosition.right,
              bottom: Math.max(menuPosition.bottom, theme.spacings.sm),
              maxHeight: Dimensions.get('window').height * 0.6,
            },
          ]}
        >
          <View
            style={[
              styles.menuButtonWrapper,
              {
                borderColor: colors.newUI.therapist.brandGreen,
              },
            ]}
          >
            <ScrollView bounces={false}>
              {menuItems.map((item, index) => (
                <React.Fragment key={item.action}>
                  <PressableNeolexon
                    onPress={() =>
                      !item.isDisabled && onActionPress(item.action)
                    }
                    style={{
                      padding: theme.spacings.md,
                      borderTopLeftRadius:
                        index === 0 ? theme.borderRadii.lg : 0,
                      borderTopRightRadius:
                        index === 0 ? theme.borderRadii.lg : 0,
                      borderBottomLeftRadius:
                        index === menuItems.length - 1
                          ? theme.borderRadii.lg
                          : 0,
                      borderBottomRightRadius:
                        index === menuItems.length - 1
                          ? theme.borderRadii.lg
                          : 0,
                      opacity: item.isDisabled ? 0.35 : 1,
                    }}
                  >
                    <Text
                      style={{
                        textAlign: 'right',
                        color: item.isDestructive
                          ? 'red'
                          : colors.newUI.therapist.textBlue100,
                      }}
                    >
                      {item.label}
                    </Text>
                  </PressableNeolexon>
                  {index < menuItems.length - 1 && (
                    <Divider color={colors.newUI.therapist.blue20} />
                  )}
                </React.Fragment>
              ))}
            </ScrollView>
          </View>
        </View>
      </RNModal>

      <Modal
        onClose={() => setErgebnisseTherapistDemo(false)}
        isVisible={ergebnisseTherapistDemo}
        title="Ergebnisse in der Demo nicht verfügbar"
        confirmActionButton={
          <Button
            label="OK"
            onPress={() => setErgebnisseTherapistDemo(!ergebnisseTherapistDemo)}
          />
        }
      >
        <Text style={{ fontSize: 18 }}>
          Registrieren Sie sich bei neolexon, um Ihre eigenen Patient:innen und
          Übungssets zu erstellen.
        </Text>
      </Modal>
    </>
  );
};
